/**
 * Mission-statement quality hints (homelab-kb #32).
 *
 * Advisory only — never blocks a save. The backend's hard floor is 20
 * characters and that is deliberately low; onboarding has to stay under five
 * minutes and a beta tester writing cold should not be stopped by a form.
 *
 * Why this exists at all: since the Phase 10D matching overhaul the mission
 * statement is the PRIMARY relevance signal. Topical fit is banded cosine
 * similarity between the client's mission embedding and the opportunity's,
 * and focus areas survive only as a short clause appended before embedding.
 * A thin mission therefore degrades matching directly.
 *
 * It also degrades the funder blurbs. Measured on prod 2026-07-27: 7 of the 8
 * grounding-guard rejections came from one client whose mission was the words
 * "Test Mission" repeated ten times. Given a profile with no content, the
 * blurb model falls back on the client's focus-area LIST as its only material
 * and attributes it to the funder — the exact leak homelab-kb #45 is about.
 * That client passed the 20-character floor with 129 characters.
 *
 * Three checks only, each cheap and explainable. Deliberately not a
 * vagueness classifier: the goal is to catch missions that are obviously
 * thin, not to grade prose.
 */

// Full state names, so "Massachusetts" in the prose counts as naming the
// state the user selected. options.js carries the abbreviations only.
const STATE_NAMES = {
  AL: 'Alabama', AK: 'Alaska', AZ: 'Arizona', AR: 'Arkansas', CA: 'California',
  CO: 'Colorado', CT: 'Connecticut', DE: 'Delaware', FL: 'Florida', GA: 'Georgia',
  HI: 'Hawaii', ID: 'Idaho', IL: 'Illinois', IN: 'Indiana', IA: 'Iowa',
  KS: 'Kansas', KY: 'Kentucky', LA: 'Louisiana', ME: 'Maine', MD: 'Maryland',
  MA: 'Massachusetts', MI: 'Michigan', MN: 'Minnesota', MS: 'Mississippi',
  MO: 'Missouri', MT: 'Montana', NE: 'Nebraska', NV: 'Nevada',
  NH: 'New Hampshire', NJ: 'New Jersey', NM: 'New Mexico', NY: 'New York',
  NC: 'North Carolina', ND: 'North Dakota', OH: 'Ohio', OK: 'Oklahoma',
  OR: 'Oregon', PA: 'Pennsylvania', RI: 'Rhode Island', SC: 'South Carolina',
  SD: 'South Dakota', TN: 'Tennessee', TX: 'Texas', UT: 'Utah', VT: 'Vermont',
  VA: 'Virginia', WA: 'Washington', WV: 'West Virginia', WI: 'Wisconsin',
  WY: 'Wyoming', DC: 'District of Columbia',
}

// Place-type nouns that signal the writer said WHERE they work even when no
// proper noun appears ("the county's lowest-income neighborhoods").
const PLACE_WORDS = /\b(count(y|ies)|cit(y|ies)|towns?|regions?|neighou?rhoods?|districts?|boroughs?|parish(es)?|villages?|metro|statewide|watershed|valley|corridor|reservation|tribal lands?)\b/i

// A mission under this reads as one line of boilerplate. The onboarding copy
// asks for 3-5 sentences; this is roughly the floor for two real ones.
export const MISSION_FULL_LENGTH = 200

// Distinct words / total words. Real prose sits well above this even when
// repetitive; "Test Mission" x10 scores 0.10.
const MIN_LEXICAL_VARIETY = 0.45
const VARIETY_MIN_WORDS = 12

function words(text) {
  return (text || '')
    .toLowerCase()
    .replace(/[^a-z0-9\s'-]/g, ' ')
    .split(/\s+/)
    .filter(Boolean)
}

/**
 * Does the mission say where the org works? Accepts the selected state (name
 * or standalone abbreviation), a place-type noun, or any proper noun that is
 * not sentence-initial — in practice a city, county, river or partner name.
 * Permissive on purpose: this should only fire on prose with no location
 * signal at all, never nag someone who already named their patch.
 */
function namesAPlace(text, stateCode) {
  if (!text) return false
  if (PLACE_WORDS.test(text)) return true

  const full = STATE_NAMES[stateCode]
  if (full && new RegExp(`\\b${full}\\b`, 'i').test(text)) return true
  if (stateCode && new RegExp(`\\b${stateCode}\\b`).test(text)) return true

  // Proper nouns: capitalised tokens that don't open a sentence.
  let sentenceStart = true
  for (const raw of text.split(/\s+/)) {
    const token = raw.replace(/^["'(]+/, '')
    if (!sentenceStart && /^[A-Z][a-z]{2,}/.test(token)) return true
    sentenceStart = /[.!?:]["')]*$/.test(raw)
  }
  return false
}

/**
 * Assess a mission statement.
 *
 * Returns { tooShort, hints: string[] }. `tooShort` mirrors the backend's
 * hard 20-character floor (the only blocking condition); `hints` are advisory
 * and ordered by how much they cost the user in match quality.
 */
export function assessMission(text, stateCode) {
  const trimmed = (text || '').trim()
  const hints = []
  if (trimmed.length < 20) {
    return { tooShort: true, hints: [] }
  }

  const tokens = words(trimmed)
  const variety = tokens.length ? new Set(tokens).size / tokens.length : 1

  if (tokens.length >= VARIETY_MIN_WORDS && variety < MIN_LEXICAL_VARIETY) {
    hints.push(
      'This looks like placeholder or repeated text. Matching reads your ' +
        'mission directly, so filler here means weak matches.'
    )
  } else if (trimmed.length < MISSION_FULL_LENGTH) {
    hints.push(
      'Add a sentence or two. Your mission is the main thing we match on, ' +
        'so a fuller description finds more.'
    )
  }

  if (!namesAPlace(trimmed, stateCode)) {
    hints.push(
      'Name where you work: city, county or region. Foundations give ' +
        'locally, so geography is one of the strongest signals we have.'
    )
  }

  return { tooShort: false, hints }
}
